import { IconArrowUp, IconAt } from '@tabler/icons-react'
import { MouseEventHandler, ReactNode, useRef, useState } from 'react'

import { AstraAvatar, AstraButton, AstraIcon, astraTwMerge } from '~/astra'

export type RichTextInputProps = {
    disabled?: boolean
    onSendClick?: MouseEventHandler
    isEmpty?: boolean
    isLoading?: boolean
    inputElement?: ReactNode
    isValid?: boolean
    shouldCollapse?: boolean
    onOpenMentionsClick?: () => void
    showCancelButton?: boolean
    onCancelClick?: () => void
    userInitials?: string
}
export const RichTextInput = ({
    disabled,
    onSendClick,
    isLoading,
    isEmpty,
    inputElement,
    isValid,
    userInitials,
    shouldCollapse = true,
    showCancelButton = false,
    onCancelClick,
    onOpenMentionsClick,
}: RichTextInputProps) => {
    const [isCollapsed, setIsCollapsed] = useState(shouldCollapse)
    const sendButtonRef = useRef<HTMLButtonElement>(null)
    const mentionButtonRef = useRef<HTMLButtonElement>(null)
    const addEmojiButtonRef = useRef<HTMLButtonElement>(null)

    return (
        <div
            className={astraTwMerge(
                'relative flex items-stretch gap-2 rounded-md border border-base bg-neutral-subtle-rest p-3 text-muted transition-all focus-within:border-[#0034DCCC] focus-within:bg-surface-base',
                isCollapsed ? 'flex-row' : 'flex-col'
            )}
        >
            <div className="flex w-full items-start gap-2">
                {userInitials && <AstraAvatar variant="initial" initials={userInitials} size="small" useColorGenerator />}
                <div
                    className="relative max-h-[100px] min-h-[22px] w-full overflow-auto outline-none"
                    onFocus={() => shouldCollapse && setIsCollapsed(false)}
                    onBlur={e => {
                        const refs: (Element | null)[] = [mentionButtonRef.current, addEmojiButtonRef.current, sendButtonRef.current]
                        // dont collapse if the blur is caused by any registered button-refs
                        if (shouldCollapse && isEmpty && !refs.includes(e.relatedTarget)) {
                            setIsCollapsed(true)
                        }
                    }}
                >
                    {inputElement}
                </div>
            </div>
            {/* The reason for row-reverse is to make the send button the first focusable element */}
            <div className="flex flex-row-reverse justify-between gap-2">
                <span className="flex items-center gap-2">
                    {showCancelButton && <AstraButton variant="critical-secondary" label="Avbryt" size="medium" disabled={isLoading} onClick={onCancelClick} />}
                    {onSendClick ? (
                        <AstraButton
                            ref={sendButtonRef}
                            variant="primary"
                            disabled={disabled || !isValid}
                            icon={<AstraIcon icon={IconArrowUp} />}
                            size="medium"
                            isLoading={isLoading}
                            onClick={onSendClick}
                        />
                    ) : (
                        <></>
                    )}
                </span>
                {!isCollapsed && (
                    <div className={'flex items-center gap-1'}>
                        <AstraButton
                            ref={mentionButtonRef}
                            variant="secondary"
                            disabled={disabled}
                            icon={<IconAt />}
                            size="small"
                            onClick={onOpenMentionsClick}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}
